import React, { useState } from "react";
import { Alert, Avatar, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth, completeUserProfile } from "../firebase";
import { endSession } from "../session";
import AuthLayout from "./AuthLayout";
import AddressField from "./AddressField";
import { useUserProfile } from "./UserProfile";

// Primer ingreso con Google: se piden los mismos datos que en "Crear cuenta" (menos email y contraseña)
const CompleteProfile = () => {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const { profile } = useUserProfile();
  const [displayName, setDisplayName] = useState(profile?.displayName || user?.displayName || "");
  const [phone, setPhone] = useState(profile?.phone || user?.phoneNumber || "");
  const [point, setPoint] = useState(profile?.point || "");
  const [errors, setErrors] = useState({});
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const next = {};
    if (!displayName.trim()) next.displayName = "Ingresá tu nombre.";
    if (phone.replace(/\D/g, "").length < 6) next.phone = "Ingresá un teléfono válido.";
    if (!point.trim()) next.point = "Elegí tu localidad.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setError("");
    try {
      await completeUserProfile(user, { displayName: displayName.trim(), phone: phone.trim(), point });
      // El perfil se actualiza en tiempo real y la app continúa sola
    } catch {
      setError("No pudimos guardar tus datos. Probá de nuevo.");
      setSaving(false);
    }
  };

  const handleOtherAccount = async () => {
    await endSession();
    navigate("/login");
  };

  return (
    <AuthLayout title="Completá tus datos" subtitle="Es tu primera vez en Caro. Necesitamos algunos datos para crear tu cuenta.">
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2.5, p: 1.5, borderRadius: 3, bgcolor: "background.default" }}>
        <Avatar src={user?.photoURL || undefined} alt={user?.displayName || ""} sx={{ width: 40, height: 40 }}>
          {(user?.displayName || user?.email || "?").charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
            {user?.displayName || "Cuenta de Google"}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap component="p">
            {user?.email}
          </Typography>
        </Box>
      </Stack>

      <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3 }}>
        <Stack spacing={2}>
          <TextField
            label="Nombre de Cliente"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            error={!!errors.displayName}
            helperText={errors.displayName}
            required
            fullWidth
          />
          <TextField
            label="Número de Teléfono"
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            required
            fullWidth
          />
          <div>
            <AddressField label="Dirección" name="point" required stacked value={point} onChange={(e) => setPoint(e.target.value)} />
            {errors.point && (
              <Typography variant="caption" color="error" sx={{ ml: 1.75 }}>
                {errors.point}
              </Typography>
            )}
          </div>
          {error && <Alert severity="error">{error}</Alert>}
          <Button type="submit" variant="contained" color="secondary" size="large" fullWidth disabled={saving}>
            {saving ? "Guardando..." : "Continuar"}
          </Button>
          <Button color="inherit" onClick={handleOtherAccount} disabled={saving}>
            Usar otra cuenta
          </Button>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default CompleteProfile;
